import {EventEmitter, Injectable} from "@angular/core";
import {FormService} from "../../../../umbrella-common/services/form.service";
import {Order, Product, StandardName} from "../../../../store/state/order.state";
import {AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {Observable} from "rxjs";
import {distinctUntilChanged, filter, first, map, takeWhile} from "rxjs/operators";
import {AppState} from "../../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {OrderActions} from "../../../../store/actions/order.actions";
import {UmbrellaValidators} from "../../../../umbrella-common/validation/umbrella-validators";
import {BaseOrderFormService} from "../../common/base-order-form.service";
import {TranslateService} from "@ngx-translate/core";
import {orderSelector} from "../../../../store/state/state.selectors";

@Injectable()
export class OutsourcedProcessFormService extends FormService implements BaseOrderFormService {

	private predefinedProcessesMap: { [formKey: string]: string[] } = {};
	private _customProcessCheckboxSelected$: EventEmitter<{ formKey: string, checked: boolean }> = new EventEmitter<{ formKey: string, checked: boolean }>();
	private controlNamesInOrder: string[] = [];

	constructor(private fb: FormBuilder, private store: Store<AppState>, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [product, predefinedProcesses]: [Product, string[]]) {
		this.initControlNamesInOrder(product, predefinedProcesses);
		this.predefinedProcessesMap[formKey] = predefinedProcesses;

		const form: FormGroup = new FormGroup({}, {
			asyncValidators: this.formValidator(product.standard.name, predefinedProcesses)
		});
		this.addControlsForPredefinedProcesses(predefinedProcesses, product, form);
		this.addCustomProcessCheckboxControl(form, product, predefinedProcesses);
		if (this.customProcessSelected(product, predefinedProcesses)) {
			this.addCustomProcessTextAreaControlWithValue(form, product.standard.name, this.getCustomProcess(product, predefinedProcesses), predefinedProcesses);
		}

		return form;
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	shouldDisplayValidationMsgForCheckboxes(product: Product, predefinedProcesses: string[]): boolean {
		const anyPredefinedProcessSelected: boolean = this.anyPredefinedProcessSelected(product, predefinedProcesses);
		if (!this.submitClicked || anyPredefinedProcessSelected || this.customProcessSelected(product, predefinedProcesses) || this.getValueFor("customOutsourcedProcess", product.standard.name) === true) {
			return false;
		}

		const customProcess = this.getValueOrNull(product.standard.name, this.getControlNamePrefix(predefinedProcesses.length));
		return customProcess == null || customProcess.trim().length == 0;
	}

	getOutsourcedProcessesControlsNames(formKey: StandardName | string): string[] {
		return Object.keys(this.getForm(formKey).controls).filter(controlName => controlName.includes("outsourced_process_"));
	}

	otherProcessIsSelected(standardName: StandardName): boolean {
		return this.getValueOrNull(standardName, 'customOutsourcedProcess') == true;
	}

	getPredefinedProcessName(textFormControlName: string, standardName: StandardName) {
		return this.predefinedProcessesMap[standardName][this.getOutsourcedProcessesQuestionIndex(textFormControlName, standardName)];
	}


	override copyAnswers(from: StandardName, to: StandardName) {
		if (this.noOutsourcedProcessSelected(to)) {
			super.copyAnswers(from, to);
		}
	}

	protected override tryCopyAnswer(controlName: string, srcForm: string, dstForm: string) {
		if (controlName === 'customOutsourcedProcess' && this.getValueFor('customOutsourcedProcess', srcForm) == true) {
			const predefinedProcesses = this.predefinedProcessesMap[dstForm];

			if (this.getFormControl(dstForm, this.getControlNamePrefix(predefinedProcesses.length)) == null) {
				this.addCustomProcessTextAreaControlWithoutValue(this.getForm(dstForm), dstForm as StandardName, predefinedProcesses);
			}
		}

		super.tryCopyAnswer(controlName, srcForm, dstForm, true);
	}

	private noOutsourcedProcessSelected(formKey: string) {
		return this.getOutsourcedProcessesControlsNames(formKey).every(controlName => !this.getValueOrNull(formKey, controlName))
	}

	customProcessCheckboxSelected$(): Observable<{ formKey: string, checked: boolean }> {
		return this._customProcessCheckboxSelected$.asObservable();
	}

	override destroySubForm(formKey: string) {
		super.destroySubForm(formKey);
		delete this.predefinedProcessesMap[formKey];
	}

	private getOutsourcedProcessesQuestionIndex(textFormControlName: string, standardName: StandardName): number {
		return Number(textFormControlName.replace('outsourced_process_', '').replace(standardName, ''));
	}

	private anyPredefinedProcessSelected(product: Product, predefinedProcesses: string[]) {
		return product.additionalQuestions.outsourcedProcesses.some(p => predefinedProcesses.includes(p));
	}

	shouldDisplayValidationMsgForCustomProcess(): boolean {
		return this.submitClicked;
	}

	private addControlsForPredefinedProcesses(predefinedProcesses: string[], product: Product, form: FormGroup) {
		predefinedProcesses.forEach((outsourcedProcess, index) => {
			const control = this.fb.control(this.getValueForOutsourcedProcess(outsourcedProcess, product));
			form.addControl(this.getControlNamePrefix(index) + product.standard.name, control);

			let split = outsourcedProcess.split('.');
			let outsourcedProcessName = split[split.length - 1];

			control.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged(), filter(selected => selected != null))
				.subscribe(selected => this.store.dispatch(OrderActions.SELECT_OUTSOURCED_PROCESS({outsourcedProcess: outsourcedProcessName, standard: product.standard.name, selected})));
		});
	}

	private addCustomProcessCheckboxControl(form: FormGroup, product: Product, predefinedProcesses: string[]) {
		const otherOutsourcedProcessControl = this.fb.control(this.getValueForCustomProcessCheckbox(product, predefinedProcesses));
		form.addControl('customOutsourcedProcess' + product.standard.name, otherOutsourcedProcessControl);

		otherOutsourcedProcessControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(checked => {
			if (checked) {
				this.addCustomProcessTextAreaControlWithValue(form, product.standard.name, this.getCustomProcess(product, predefinedProcesses), predefinedProcesses);
			} else {
				const controlNamePrefix = this.getControlNamePrefix(predefinedProcesses.length);
				const customProcessFormControl = this.getFormControl(product.standard.name, controlNamePrefix);

				if (customProcessFormControl != null) {
					const customProcess = this.getValueFor(controlNamePrefix, product.standard.name);
					form.removeControl(controlNamePrefix + product.standard.name);
					this.store.dispatch(OrderActions.SELECT_OUTSOURCED_PROCESS({selected: false, standard: product.standard.name, outsourcedProcess: customProcess}));
				}
			}

			this._customProcessCheckboxSelected$.emit({formKey: product.standard.name, checked});
		});
	}

	private addCustomProcessTextAreaControlWithValue(form: FormGroup, formKey: StandardName, value: string, predefinedProcesses: string[]) {
		const textAreaControl = this.fb.control(value, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]);
		form.addControl(this.getControlNamePrefix(predefinedProcesses.length) + formKey, textAreaControl);
		textAreaControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(formKey)), distinctUntilChanged()).subscribe(op => this.store.dispatch(OrderActions.SELECT_CUSTOM_OUTSOURCED_PROCESS({
			standard: formKey,
			outsourcedProcess: op,
			predefinedProcesses
		})));
	}

	private addCustomProcessTextAreaControlWithoutValue(form: FormGroup, formKey: StandardName, predefinedProcesses: string[]) {
		const textAreaControl = this.fb.control(null, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]);
		form.addControl(this.getControlNamePrefix(predefinedProcesses.length) + formKey, textAreaControl);
		textAreaControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(formKey)), distinctUntilChanged()).subscribe(op => this.store.dispatch(OrderActions.SELECT_CUSTOM_OUTSOURCED_PROCESS({
			standard: formKey,
			outsourcedProcess: op,
			predefinedProcesses
		})));
	}

	private getValueForOutsourcedProcess(outsourcedProcess: string, product: Product) {
		return product.additionalQuestions.outsourcedProcesses.includes(outsourcedProcess) || null;
	}

	private getControlNamePrefix(outsourcedProcessIndex: number) {
		return `outsourced_process_${outsourcedProcessIndex}`;
	}

	private customProcessSelected(product: Product, predefinedProcesses: string[]) {
		return product.additionalQuestions.outsourcedProcesses.filter(op => !predefinedProcesses.includes(op)).length > 0;
	}

	private getValueForCustomProcessCheckbox(product: Product, predefinedProcesses: string[]) {
		return product.additionalQuestions.outsourcedProcesses.filter(op => !predefinedProcesses.includes(op)).length > 0 || null;
	}

	private getCustomProcess(product: Product, predefinedProcesses: string[]): string {
		return product.additionalQuestions.outsourcedProcesses.find(op => !predefinedProcesses.includes(op));
	}

	private anyProcessSelected(formKey): boolean {
		return this.predefinedProcessesMap[formKey].map((predefinedProcess, index) => {
				return this.getFormControl(formKey, this.getControlNamePrefix(index)).value == true ||
					this.getFormControl(formKey, 'customOutsourcedProcess').value == true;
			}
		).reduce((f1, f2) => f1 || f2, false);
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().forEach(formKey => {
			const product = order.products.find(p => p.standard.name === formKey);
			product.additionalQuestions.outsourcedProcesses = this.getOutsourcedProcesses(product.standard.name);
		});

		if (order.products.some(p => p.standard.name == StandardName.ISO_27701)) {
			let iso27701 = order.products.find(p => p.standard.name == StandardName.ISO_27701);
			iso27701.additionalQuestions.outsourcedProcesses = this.getOutsourcedProcesses(StandardName.ISO_27001);
		}

		return order;
	}

	private getOutsourcedProcesses(standardName: StandardName) {
		const controlNames = this.getOutsourcedProcessesControlsNames(standardName);
		let controlNamesWithoutCustomProcess: string[] = [];

		if (this.otherProcessIsSelected(standardName)) {
			controlNamesWithoutCustomProcess = controlNames.slice(0, controlNames.length - 1);
		} else {
			controlNamesWithoutCustomProcess = controlNames;
		}

		const outsourcedProcesses: string[] = controlNamesWithoutCustomProcess.filter(controlName => {
			return this.getValueOrNull(standardName, controlName) == true;
		}).map(controlName => {
			return this.getPredefinedProcessName(controlName, standardName);
		});

		if (this.otherProcessIsSelected(standardName)) {
			const other: string = this.getValueOrNull(standardName, controlNames[controlNames.length - 1]);
			outsourcedProcesses.push(other);
		}

		return outsourcedProcesses;
	}

	private initControlNamesInOrder(product: Product, predefinedProcesses: string[]) {
		for (let i = 0; i < predefinedProcesses.length; i++) {
			this.controlNamesInOrder.push(this.getControlNamePrefix(i) + product.standard.name);
		}

		this.controlNamesInOrder.push('customOutsourcedProcess' + product.standard.name)
		this.controlNamesInOrder.push(this.getControlNamePrefix(predefinedProcesses.length) + product.standard.name);
	}

	override isInvalid(formKey: string, formControlName: string) {
		if (formControlName == 'customOutsourcedProcess'
			|| formControlName == this.getControlNamePrefix(this.predefinedProcessesMap[formKey].length) + formKey) {
			return super.isInvalid(formKey, formControlName);
		}

		const control = this.getFormControl(formKey, formControlName);
		return control && !this.anyProcessSelected(formKey);
	}

	private formValidator(standard: StandardName, predefinedProcesses: string[]): AsyncValidatorFn {
		return () => {
			return this.store.select(orderSelector).pipe(first(), map(order => order.products.find(p => p.standard.name === standard)), map(product => {
				let invalid = this.shouldDisplayValidationMsgForCheckboxes(product, predefinedProcesses);
				return invalid ?
					{"invalid": true} as ValidationErrors :
					null;
			}));
		};
	}
}
